import { BaseService } from '../base/BaseService';

import type { Database, Json } from '@/integrations/supabase/types';
import type {
  ConceptDeliveryIntent,
  ConceptFormat,
  ConceptProof,
  ConceptStatus,
  ConceptVisualDirection,
} from '@/lib/briefs/concept-contract';
import { CONCEPT_FORMATS } from '@/lib/briefs/concept-contract';
import { VISUAL_DIRECTION_TEXT_PLAN_KEYS } from '@/lib/content-doctrine/piece';

export type BriefConceptIntent = {
  intent: ConceptDeliveryIntent;
  platform: string | null;
  position: number;
};

export type BriefConceptReferenceUrl = {
  id: string;
  url: string;
  createdAt: string;
};

export type BriefConcept = {
  id: string;
  briefId: string;
  audienceId: string | null;
  angleId: string | null;
  rationale: string | null;
  hook: string | null;
  summary: string | null;
  proof: ConceptProof;
  cta: string | null;
  visualDirection: ConceptVisualDirection;
  format: ConceptFormat;
  status: ConceptStatus;
  version: number;
  grounding: Json | null;
  anatomy: Json | null;
  intents: BriefConceptIntent[];
  referenceUrls: BriefConceptReferenceUrl[];
};

export type BriefConceptWrite = {
  audienceId: string | null;
  angleId: string | null;
  rationale: string | null;
  hook: string | null;
  summary: string | null;
  proof: ConceptProof;
  cta: string | null;
  visualDirection: ConceptVisualDirection;
  format: ConceptFormat;
  intents: ReadonlyArray<Pick<BriefConceptIntent, 'intent' | 'platform'>>;
};

type BriefConceptSnapshot = {
  task: {
    id: string;
    project_id: string;
    audience_id: string | null;
    angle_id: string | null;
    rationale: string | null;
    hook: string | null;
    summary: string | null;
    proof: unknown;
    cta: string | null;
    visual_direction: unknown;
    format: string | null;
    status: string;
    version: number;
    grounding: Json | null;
    anatomy: Json | null;
  };
  intents: Array<{ intent: string; platform: string | null; position: number }>;
  reference_urls: Array<{ id: string; url: string; created_at: string }>;
};

type BriefConceptRpcName = keyof Pick<
  Database['public']['Functions'],
  | 'get_brief_concept'
  | 'create_brief_concept_idempotent'
  | 'update_brief_concept_once'
  | 'archive_brief_concept'
  | 'restore_brief_concept'
>;
type BriefConceptRpcArgs<Name extends BriefConceptRpcName> =
  Database['public']['Functions'][Name]['Args'];

/**
 * Supabase generates defaulted nullable SQL parameters as optional `string`,
 * which loses the distinct "pass null to clear" operation. The Brief Concept
 * RPC deliberately uses that distinction: an omitted update argument accepts
 * its SQL default, while an explicit null clears the persisted scalar. Keep
 * the correction at this typed RPC boundary rather than pretending null is a
 * generated client value everywhere else.
 */
type NullableDefaultScalarArg =
  'p_angle_id' | 'p_audience_id' | 'p_cta' | 'p_format' | 'p_hook' | 'p_rationale' | 'p_summary';

type WidenNullableDefaultArgs<Args> = Omit<Args, NullableDefaultScalarArg> & {
  [Key in NullableDefaultScalarArg]?: Key extends keyof Args ? Args[Key] | null : never;
};

type BriefConceptCreateArgs = WidenNullableDefaultArgs<
  Database['public']['Functions']['create_brief_concept_idempotent']['Args']
>;
type BriefConceptUpdateArgs = WidenNullableDefaultArgs<
  Database['public']['Functions']['update_brief_concept_once']['Args']
>;
type BriefConceptRpcBoundaryArgs<Name extends BriefConceptRpcName> =
  Name extends 'create_brief_concept_idempotent'
    ? BriefConceptCreateArgs
    : Name extends 'update_brief_concept_once'
      ? BriefConceptUpdateArgs
      : BriefConceptRpcArgs<Name>;
type BriefConceptWriteArgs = Pick<
  BriefConceptCreateArgs,
  | 'p_angle_id'
  | 'p_audience_id'
  | 'p_cta'
  | 'p_format'
  | 'p_hook'
  | 'p_intents'
  | 'p_proof'
  | 'p_rationale'
  | 'p_summary'
  | 'p_visual_direction'
>;

function asJson(value: unknown): Json {
  return value as Json;
}

function normalizeProof(value: unknown): ConceptProof {
  const proof = value as { kind?: unknown; ref?: unknown; quote?: unknown } | null;
  if (
    !proof ||
    typeof proof !== 'object' ||
    typeof proof.kind !== 'string' ||
    !['metric', 'quote', 'case', 'artefact', 'none'].includes(proof.kind) ||
    (proof.ref !== null && typeof proof.ref !== 'string') ||
    (proof.quote !== null && typeof proof.quote !== 'string') ||
    Object.keys(proof).length !== 3
  ) {
    throw new Error('Concept proof violates the v1 persistence contract.');
  }

  const kind = proof.kind as ConceptProof['kind'];
  const ref = typeof proof.ref === 'string' ? proof.ref.trim() : null;
  if (proof.ref !== null && !ref) {
    throw new Error('Concept proof references must be null or non-empty strings.');
  }
  if (kind === 'none') {
    if (proof.ref !== null || proof.quote !== null) {
      throw new Error('A none Concept proof cannot include a reference or quote.');
    }
    return { kind: 'none', ref: null, quote: null };
  }

  const quote = typeof proof.quote === 'string' ? proof.quote.trim() : '';
  if (!quote) {
    throw new Error('A non-none Concept proof needs a non-empty quote.');
  }
  return { kind, ref, quote };
}

function parseProof(value: unknown): ConceptProof {
  try {
    return normalizeProof(value);
  } catch {
    throw new Error('Stored Concept proof violates the v1 persistence contract.');
  }
}

function normalizeFormat(value: unknown): ConceptFormat {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string')
    throw new Error('Concept format must be null or a supported v1 format.');
  const format = value.trim();
  if (!format) return null;
  if (!CONCEPT_FORMATS.includes(format as Exclude<ConceptFormat, null>)) {
    throw new Error('Concept format must be single, carousel, video, text, or null.');
  }
  return format as Exclude<ConceptFormat, null>;
}

const TEXT_PLAN_KEYS: ReadonlySet<string> = new Set(VISUAL_DIRECTION_TEXT_PLAN_KEYS);

function parseVisualDirection(value: unknown): ConceptVisualDirection {
  const direction = value as ConceptVisualDirection | null;
  if (
    !direction ||
    typeof direction !== 'object' ||
    ![
      'product_hero',
      'in_use',
      'scene',
      'portrait',
      'artefact',
      'diagram',
      'screenshot',
      'none',
    ].includes(direction.intent) ||
    typeof direction.subject !== 'string' ||
    typeof direction.setting !== 'string' ||
    !['upper-third', 'lower-third', 'left-column', 'right-column', 'none'].includes(
      direction.negativeSpace,
    ) ||
    !direction.textPlan ||
    !['overlay', 'baked', 'none'].includes(direction.textPlan.mode) ||
    (direction.textPlan.headline !== null && typeof direction.textPlan.headline !== 'string') ||
    (direction.textPlan.sourceLine !== undefined &&
      direction.textPlan.sourceLine !== null &&
      typeof direction.textPlan.sourceLine !== 'string') ||
    (direction.refusal !== null && typeof direction.refusal !== 'string') ||
    Object.keys(direction).length !== 6 ||
    Object.keys(direction.textPlan).some((key) => !TEXT_PLAN_KEYS.has(key))
  ) {
    throw new Error('Stored Concept visual direction violates the v1 persistence contract.');
  }
  return direction;
}

function normalizeIntents(input: BriefConceptWrite['intents']): Json {
  if (!Array.isArray(input) || input.length === 0) {
    throw new Error('A Concept needs at least one delivery intent.');
  }

  const seen = new Set<string>();
  const intents = input.map((value) => {
    if ((value.intent !== 'organic' && value.intent !== 'paid') || seen.has(value.intent)) {
      throw new Error('Concept intents must be unique organic or paid values.');
    }
    seen.add(value.intent);
    const platform = value.platform?.trim() || null;
    return { intent: value.intent, platform };
  });
  return intents as Json;
}

function normalizeReferenceUrls(input: string[]): Json {
  if (input.length > 5) {
    throw new Error('A Concept can have at most five reference URLs.');
  }
  const seen = new Set<string>();
  const urls = input.map((value) => value.trim());
  for (const url of urls) {
    if (!/^https?:\/\/\S+$/i.test(url) || seen.has(url)) {
      throw new Error('Reference URLs must be unique absolute HTTP(S) URLs.');
    }
    seen.add(url);
  }
  return urls as Json;
}

function mapConcept(snapshot: BriefConceptSnapshot): BriefConcept {
  const task = snapshot.task;
  if (!['todo', 'drafting', 'review', 'approved', 'failed', 'archived'].includes(task.status)) {
    throw new Error('Stored Concept has an unsupported planning status.');
  }

  return {
    id: task.id,
    briefId: task.project_id,
    audienceId: task.audience_id,
    angleId: task.angle_id,
    rationale: task.rationale,
    hook: task.hook,
    summary: task.summary,
    proof: parseProof(task.proof),
    cta: task.cta,
    visualDirection: parseVisualDirection(task.visual_direction),
    format: normalizeFormat(task.format),
    status: task.status as ConceptStatus,
    version: task.version,
    grounding: task.grounding,
    anatomy: task.anatomy,
    intents: (snapshot.intents ?? []).map((intent) => {
      if (intent.intent !== 'organic' && intent.intent !== 'paid') {
        throw new Error('Stored Concept has an unsupported delivery intent.');
      }
      return { intent: intent.intent, platform: intent.platform, position: intent.position };
    }),
    referenceUrls: (snapshot.reference_urls ?? []).map((reference) => ({
      id: reference.id,
      url: reference.url,
      createdAt: reference.created_at,
    })),
  };
}

export class BriefConceptEditorService extends BaseService {
  private async invoke<Name extends BriefConceptRpcName>(
    name: Name,
    args: BriefConceptRpcBoundaryArgs<Name>,
  ): Promise<BriefConcept> {
    await this.ensureAuthenticatedOwner();
    // Supabase's generated overload cannot correlate a union RPC name and its
    // argument member. Keep that limitation at this one typed adapter instead
    // of erasing both the RPC name and arguments at every call site. The
    // boundary also preserves explicit-null clearing for SQL defaulted nullable
    // scalars; generated optional arguments alone cannot express that truth.
    const rpc = this.client.rpc.bind(this.client) as unknown as <
      RpcName extends BriefConceptRpcName,
    >(
      rpcName: RpcName,
      rpcArgs: BriefConceptRpcBoundaryArgs<RpcName>,
    ) => Promise<{
      data: Database['public']['Functions'][RpcName]['Returns'] | null;
      error: Error | null;
    }>;
    const { data, error } = await rpc(name, args);
    if (error) throw error;
    if (!data) throw new Error('Brief Concept not found.');
    return mapConcept(data as unknown as BriefConceptSnapshot);
  }

  get(briefId: string, projectTaskId: string): Promise<BriefConcept> {
    return this.invoke('get_brief_concept', {
      p_brief_id: briefId,
      p_project_task_id: projectTaskId,
    });
  }

  create(
    input: BriefConceptWrite & {
      briefId: string;
      commandId: string;
      referenceUrls?: string[];
    },
  ): Promise<BriefConcept> {
    return this.invoke('create_brief_concept_idempotent', {
      p_command_id: input.commandId,
      p_brief_id: input.briefId,
      ...this.writeArgs(input),
      p_reference_urls: normalizeReferenceUrls(input.referenceUrls ?? []),
    });
  }

  update(
    input: BriefConceptWrite & {
      briefId: string;
      projectTaskId: string;
      expectedVersion: number;
      referenceUrls?: string[];
    },
  ): Promise<BriefConcept> {
    const args: BriefConceptUpdateArgs = {
      p_brief_id: input.briefId,
      p_project_task_id: input.projectTaskId,
      p_expected_version: input.expectedVersion,
      ...this.writeArgs(input),
    };
    if (input.referenceUrls !== undefined) {
      args.p_reference_urls = normalizeReferenceUrls(input.referenceUrls);
    }
    return this.invoke('update_brief_concept_once', args);
  }

  archive(input: {
    briefId: string;
    projectTaskId: string;
    expectedVersion: number;
  }): Promise<BriefConcept> {
    return this.invoke('archive_brief_concept', {
      p_brief_id: input.briefId,
      p_project_task_id: input.projectTaskId,
      p_expected_version: input.expectedVersion,
    });
  }

  restore(input: {
    briefId: string;
    projectTaskId: string;
    expectedVersion: number;
  }): Promise<BriefConcept> {
    return this.invoke('restore_brief_concept', {
      p_brief_id: input.briefId,
      p_project_task_id: input.projectTaskId,
      p_expected_version: input.expectedVersion,
    });
  }

  private writeArgs(input: BriefConceptWrite): BriefConceptWriteArgs {
    return {
      p_audience_id: input.audienceId,
      p_angle_id: input.angleId,
      p_rationale: input.rationale,
      p_hook: input.hook,
      p_summary: input.summary,
      p_proof: asJson(normalizeProof(input.proof)),
      p_cta: input.cta,
      p_visual_direction: asJson(input.visualDirection),
      p_format: normalizeFormat(input.format),
      p_intents: normalizeIntents(input.intents),
    };
  }
}

export const briefConceptEditorService = new BriefConceptEditorService();
